import { useCallback, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';
import type { Database } from '../types/database';

import { CHAT_EVENTS, useTracking } from '@/hooks/useTracking';
import { useGemBalance } from './useGemBalance';

type Message = Database['eh']['Tables']['messages']['Row'];
type Character = Database['eh']['Tables']['characters']['Row'];

interface TTSState {
  isLoading: boolean;
  isPlaying: boolean;
  error: string | null;
}

interface TTSResult {
  success: boolean;
  limitReached?: boolean;
  limitType?: 'low_gem_balance' | 'rate_limit';
  error?: string;
}

export const useTTS = () => {
  const { trackEvent } = useTracking();
  const { refreshBalance } = useGemBalance();

  const [ttsStates, setTTSStates] = useState<Record<string, TTSState>>({});
  const audioRefs = useRef<Record<string, HTMLAudioElement>>({});
  const playPromiseRefs = useRef<Record<string, Promise<void> | null>>({});

  const updateTTSState = useCallback((messageId: string, updates: Partial<TTSState>) => {
    setTTSStates(prev => ({
      ...prev,
      [messageId]: { ...prev[messageId], ...updates }
    }));
  }, []);

  const getTTSState = useCallback((messageId: string): TTSState => {
    return ttsStates[messageId] || { isLoading: false, isPlaying: false, error: null };
  }, [ttsStates]);

  const playTTS = useCallback(async (message: Message, character?: Character | null): Promise<TTSResult> => {
    if (!message.msg_text || message.sender_id) return { success: false, error: 'Invalid message' }; // Only AI messages
    
    const messageId = message.id;
    const currentState = getTTSState(messageId);
    
    // If already playing, pause it (but wait for any pending play promise)
    // https://developer.chrome.com/blog/play-request-was-interrupted
    if (currentState.isPlaying) {
      const audio = audioRefs.current[getAudioKey(message, character)];
      const playPromise = playPromiseRefs.current[messageId];
      
      if (audio && playPromise) {
        // Wait for the play promise to resolve before pausing
        try {
          await playPromise;
          audio.pause();
        } catch (_err) {
          // Play was already rejected, safe to pause
          audio.pause();
        }
      } else if (audio) {
        audio.pause();
      }
      
      updateTTSState(messageId, { isPlaying: false });
      playPromiseRefs.current[messageId] = null;
      return { success: true };
    }

    // If audio already exists for message and voice, just play it with promise handling
    if (audioRefs.current[getAudioKey(message, character)]) {
      const audio = audioRefs.current[getAudioKey(message, character)];
      updateTTSState(messageId, { isPlaying: true });
      
      const playPromise = audio.play();
      playPromiseRefs.current[messageId] = playPromise;
      
      playPromise
        .then(() => {
          // Playback started successfully
          updateTTSState(messageId, { isPlaying: true });
        })
        .catch(err => {
          trackEvent(CHAT_EVENTS.CHAT_TTS_ERROR, {
            messageId,
            characterId: character?.id,
            error: err instanceof Error ? err.message : 'Failed to play audio',
          });
          // Playback failed
          updateTTSState(messageId, { isPlaying: false, error: 'Failed to play audio' });
          playPromiseRefs.current[messageId] = null;
        });
      
      return { success: true };
    }

    updateTTSState(messageId, { isLoading: true, error: null });

    try {
      // Get auth token for the request
      const session = await supabase.auth.getSession();
      if (!session.data.session?.access_token) {
        throw new Error('No authentication token available');
      }

      const { id, chat_id, msg_text } = message;
      // Call the TTS edge function directly to get streaming audio
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/eh-chat-tts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.data.session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: { id, chat_id, msg_text },
          userId: session.data.session.user.id,
          ...(character ? {
            character: {
              gender: character.gender,
              url_slug: character.url_slug,
              voice: character?.voice,
              voice_details: character?.voice_details,
            },
          } : {}),
        }),
      });

      if (!response.ok) {
        // Handle 402 Payment Required specifically
        if (response.status === 402) {
          updateTTSState(messageId, { isLoading: false });
          return { 
            success: false, 
            limitReached: true, 
            limitType: 'low_gem_balance' // normalize different codes to low_gem_balance
          };
        }
        
        const errorData = await response.json().catch((e) => {
          trackEvent(CHAT_EVENTS.CHAT_TTS_ERROR, {
            messageId,
            characterId: character?.id,
            error: e instanceof Error ? e.message : 'Failed to parse TTS response',
          });
          return { error: 'Failed to generate speech' };
        });
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      // The response should be streaming audio
      const blob = await response.blob();
      const audioUrl = URL.createObjectURL(blob);
      
      const audio = new Audio(audioUrl);
      audioRefs.current[getAudioKey(message, character)] = audio;

      audio.onended = () => {
        updateTTSState(messageId, { isPlaying: false });
        URL.revokeObjectURL(audioUrl); // Clean up the blob URL
        playPromiseRefs.current[messageId] = null;
      };

      audio.onerror = () => {
        trackEvent(CHAT_EVENTS.CHAT_TTS_ERROR, {
          messageId,
          characterId: character?.id,
          error: 'Failed to play audio',
        });
        updateTTSState(messageId, { isLoading: false, error: 'Failed to play audio' });
        URL.revokeObjectURL(audioUrl); // Clean up the blob URL
        playPromiseRefs.current[messageId] = null;
      };

      audio.oncanplay = () => {
        updateTTSState(messageId, { isLoading: false });
        
        // Properly handle the play promise
        const playPromise = audio.play();
        playPromiseRefs.current[messageId] = playPromise;
        
        playPromise
          .then(() => {
            // Playback started successfully
            updateTTSState(messageId, { isPlaying: true });
          })
          .catch(err => {
            // Playback failed (could be interrupted by user or autoplay policy)
            updateTTSState(messageId, { isLoading: false, error: 'Failed to start playback' });
            playPromiseRefs.current[messageId] = null;
            if (import.meta.env.DEV) {
              console.warn('[DEV] Audio play interrupted:', err);
            }
          });
      };

      audio.load();

      trackEvent(CHAT_EVENTS.CHAT_TTS_SUCCESS, {
        messageId,
        characterId: character?.id,
        voiceId: character?.voice,
      });

      // NOTE: even if audio was already generated before (balance not updated). May be improved later.
      await refreshBalance();
      
      return { success: true };
    } catch (err) {
      updateTTSState(messageId, { 
        isLoading: false, 
        error: import.meta.env.DEV && err instanceof Error ? `[DEV] ${err.message}` : 'Audio failed. Please try again.' 
      });
      
      trackEvent(CHAT_EVENTS.CHAT_TTS_ERROR, {
        messageId,
        characterId: character?.id,
        voiceId: character?.voice,
        error: err instanceof Error ? err.message : 'Failed to generate or play audio',
      });
      
      return { 
        success: false, 
        error: import.meta.env.DEV && err instanceof Error ? `[DEV] ${err.message}` : 'Failed to generate or play audio' 
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getTTSState, updateTTSState, refreshBalance]);

  const stopAllAudio = useCallback(() => {
    Object.entries(audioRefs.current).forEach(([messageId, audio]) => {
      const playPromise = playPromiseRefs.current[messageId];
      
      if (playPromise) {
        // Wait for play promise before pausing
        playPromise
          .then(() => audio.pause())
          .catch(() => audio.pause()); // If play failed, safe to pause anyway
      } else {
        audio.pause();
      }
      
      updateTTSState(messageId, { isPlaying: false });
      playPromiseRefs.current[messageId] = null;
    });
  }, [updateTTSState]);

  return {
    playTTS,
    getTTSState,
    stopAllAudio,
  };
};

function getAudioKey(message: Message, character?: Character | null) {
  return `${message.id}_${character?.voice ?? ''}`;
}